import { css } from '@emotion/react';
import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

type StepState = {
    readonly isActive?: boolean;
    readonly isComplete?: boolean;
    readonly isDisabled?: boolean;
    readonly label: string;
};
export type SidebarStepProps = StepState & {
    readonly numberOfRequiredActions?: number;
    readonly onClick?: () => void;
};

export type RootStepProps = StepState & {
    readonly stepNumber: number;
};

const BaseStepContainer = styled.div<{ readonly isDisabled: boolean }>`
    display: grid;
    min-height: 2rem;
    grid-template-columns: 1rem 1fr 1.5rem;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    border-radius: 0.5rem;
    user-select: none;
    font-family: 'FT Regola Neue', sans-serif;
    font-weight: 500;
    font-size: 0.75rem;
    line-height: 0.875rem;
    cursor: pointer;
    color: #6e6e6c;
    ${({ isDisabled }) =>
        isDisabled &&
        css`
            opacity: 0.5;
            cursor: not-allowed;
        `}
`;

const StepContainer = styled(BaseStepContainer)({
    '&:hover': {
        background: '#f1f1f0',
    },
});

const RootContainer = styled(BaseStepContainer)<{ readonly isActive: boolean; readonly isDisabled?: boolean }>`
    cursor: default;
    ${({ isActive }) =>
        isActive &&
        css`
            background-color: #fbfbff;
            font-weight: 600;
        `}
`;

const pickIconColor = ({
    isComplete,
    isDisabled,
}: {
    readonly isComplete: boolean;
    readonly isDisabled: boolean;
}): string => {
    if (isDisabled) {
        return '#C7C7C5';
    }
    if (isComplete) {
        return '#6E6E6C';
    }

    return '#fc531f';
};

const RootIconWrapper = styled.div<{ readonly isComplete: boolean; readonly isDisabled: boolean }>(
    ({ isComplete, isDisabled }) => {
        return {
            alignItems: 'center',
            backgroundColor: pickIconColor({ isComplete, isDisabled }),
            borderRadius: '1rem',
            display: 'flex',
            fontFamily: 'FT Regola Neue',
            fontSize: '0.875rem',
            gridColumn: 1,
            height: '1rem',
            justifyContent: 'center',
            width: '1rem',
        };
    },
);

const RootStepCounter = styled.span({
    color: '#fbfbff',
    fontWeight: 500,
});

const StepActionContainer = styled.div({
    alignItems: 'center',
    background: '#f1f1f0',
    border: '0.0625rem #dadad8 solid',
    borderRadius: '9999rem',
    display: 'inline-flex',
    height: '1.125rem',
    justifyContent: 'flex-start',
    paddingBottom: '0.0625rem',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    paddingTop: '0.0625rem',
    width: '1.4375rem',
});

const StepActionCounter = styled.div({
    color: '#505052',
    fontFamily: "'FT Regola Neue'",
    fontSize: '0.625rem',
    fontWeight: 600,
    lineHeight: '1rem',
    textAlign: 'center',
    wordWrap: 'break-word',
});

const Label = styled.span({
    color: '#040f16',
    fontSize: '0.8125rem',
    fontWeight: 500,
    gridColumn: '2',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const StepLabel = styled(Label)<{ readonly isActive: boolean; readonly isComplete: boolean }>`
    color: ${({ isComplete }) => (isComplete ? '#505052' : '#040f16')};
    ${({ isActive }) =>
        isActive &&
        css`
            text-decoration: underline;
            text-decoration-color: #fc531f;
        `}
`;

const SvgContainer = styled.div({
    alignItems: 'center',
    display: 'flex',
    gridColumn: '3',
    justifyContent: 'flex-end',
});

const CheckIcon: FunctionComponent = () => (
    // biome-ignore lint/a11y/noSvgWithoutTitle: suppressed due to migration
    <svg fill="none" height="1rem" viewBox="0 0 16 16" width="1rem" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M10 3L4.5 8.5L2 6"
            stroke="#b2b2b0"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="0.0857rem"
        />
    </svg>
);

const StepCompleteIcon: FunctionComponent = () => (
    // biome-ignore lint/a11y/noSvgWithoutTitle: suppressed due to migration
    <svg fill="none" height="14" viewBox="0 0 14 14" width="14" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_10248_2295)">
            <path
                d="M4.37484 6.99975L6.12484 8.74975L9.62484 5.24975M12.8332 6.99975C12.8332 10.2214 10.2215 12.8331 6.99984 12.8331C3.77818 12.8331 1.1665 10.2214 1.1665 6.99975C1.1665 3.77808 3.77818 1.16641 6.99984 1.16641C10.2215 1.16641 12.8332 3.77808 12.8332 6.99975Z"
                stroke="#00A41B"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.6"
            />
        </g>
        <defs>
            <clipPath id="clip0_10248_2295">
                <rect fill="white" height="14" width="14" />
            </clipPath>
        </defs>
    </svg>
);

export const SidebarStep: FunctionComponent<SidebarStepProps> = ({
    isActive = false,
    isComplete = false,
    isDisabled = false,
    label,
    numberOfRequiredActions,
    onClick,
}) => {
    const handleOnClick = (): void => {
        if (isDisabled) {
            return;
        }
        onClick?.();
    };

    return (
        <StepContainer isDisabled={isDisabled} onClick={handleOnClick}>
            <StepLabel isActive={isActive} isComplete={isComplete}>
                {label}
            </StepLabel>
            {is.nullOrUndefined(numberOfRequiredActions) || isComplete ? null : (
                <StepActionContainer>
                    <StepActionCounter>{numberOfRequiredActions}</StepActionCounter>
                </StepActionContainer>
            )}
            {isComplete && (
                <SvgContainer>
                    <CheckIcon />
                </SvgContainer>
            )}
        </StepContainer>
    );
};

export const RootStep: FunctionComponent<RootStepProps> = ({
    isActive = false,
    isComplete = false,
    isDisabled = false,
    label,
    stepNumber,
}) => {
    return (
        <RootContainer isActive={isActive} isDisabled={isDisabled}>
            <RootIconWrapper isComplete={isComplete} isDisabled={isDisabled}>
                <RootStepCounter>{stepNumber}</RootStepCounter>
            </RootIconWrapper>
            <Label>{label}</Label>
            {isComplete && (
                <SvgContainer>
                    <StepCompleteIcon />
                </SvgContainer>
            )}
        </RootContainer>
    );
};
